/**
 * modelTier.ts — single source of truth for model-ID → capability-tier mapping.
 *
 * Customer-facing UIs MUST use getCapabilityTier() to display a human label
 * instead of raw model identifiers.  Only platform-admin / fleet-internal
 * debug views may show raw IDs (gate by audience check at call site).
 *
 * Founder rule 2026-06-01: "Do not show what model used in the frontend,
 * just Quick, Fast, Large, Small … show based on model type/property."
 * See memory: feedback_model_label_only.md
 *
 * To add a new model: append one entry to MODEL_TIER_MAP below.
 * The frontend automatically picks up the label from the next build.
 */

export type CapabilityTier = "Quick" | "Fast" | "Large" | "Small";

/**
 * Canonical mapping.  Keys are the full LiteLLM-style model IDs sent by the
 * backend (provider/model-name).  Bare model names (without provider prefix)
 * are also matched via the fallback pattern matching below.
 */
const MODEL_TIER_MAP: Record<string, CapabilityTier> = {
  // Anthropic Claude
  "claude/claude-opus-4-7":      "Large",
  "claude/claude-opus-4-8":      "Large",
  "claude/claude-sonnet-4-6":    "Fast",
  "claude/claude-haiku-4-5":     "Quick",

  // Google Gemini
  "gemini/gemini-2.5-pro":       "Fast",
  "gemini/gemini-2.5-flash":     "Quick",
  "gemini/gemini-1.5-flash-8b":  "Small",

  // OpenAI
  "openai/gpt-4o":               "Fast",
  "openai/gpt-4o-mini":          "Small",
};

/**
 * Returns the capability tier label for a given model ID.
 *
 * Lookup order:
 *   1. Exact match against MODEL_TIER_MAP (handles "provider/model" keys).
 *   2. Pattern-based fallback for bare / versioned model names (no provider prefix).
 *   3. Unknown → logs a console.warn and returns "Fast" as a safe default.
 *
 * @param modelId  Raw model identifier from the backend (e.g. "claude/claude-opus-4-7",
 *                 "claude-opus-4-7", or "opus").
 * @returns        One of "Quick" | "Fast" | "Large" | "Small".
 */
export function getCapabilityTier(modelId: string): CapabilityTier {
  if (!modelId) return "Fast";

  // 1. Exact match (canonical form: "provider/model")
  const exact = MODEL_TIER_MAP[modelId];
  if (exact) return exact;

  // 2. Substring / pattern fallback so bare names still resolve
  const lower = modelId.toLowerCase();

  if (lower.includes("opus"))                    return "Large";
  if (lower.includes("sonnet"))                  return "Fast";
  if (lower.includes("gemini-2.5-pro"))          return "Fast";
  if (lower.includes("gemini-2.5-flash"))        return "Quick";
  if (lower.includes("gemini-1.5-flash-8b"))     return "Small";
  if (lower.includes("flash-8b"))                return "Small";
  if (lower.includes("flash"))                   return "Quick";
  if (lower.includes("haiku"))                   return "Quick";
  if (lower.includes("gpt-4o-mini"))             return "Small";
  if (lower.includes("gpt-4o"))                  return "Fast";

  // 3. Unknown model — warn and return a safe default
  console.warn(`[modelTier] Unknown model ID: "${modelId}" — defaulting to "Fast"`);
  return "Fast";
}

/** Tier badge colours (reuse in any component that renders the tier pill). */
export const TIER_COLORS: Record<CapabilityTier, string> = {
  Large: "var(--brand-gold-primary)",
  Fast:  "var(--brand-status-info)",
  Quick: "var(--brand-status-success)",
  Small: "var(--brand-text-secondary)",
};
